import { captureException } from "@sentry/react";
import { useRef } from "react";

import { cn } from "@/lib/utils";

const MAX_REPORTED = 50;
const reportedSrcs = new Set<string>();

function recordReport(src: string) {
	if (reportedSrcs.has(src)) return false;
	if (reportedSrcs.size >= MAX_REPORTED) {
		const first = reportedSrcs.values().next().value;
		if (first !== undefined) reportedSrcs.delete(first);
	}
	reportedSrcs.add(src);
	return true;
}

type ImageProps = React.ComponentProps<"img"> & {
	fallbackSrc?: string;
};

function Image({
	alt,
	className,
	onError,
	fallbackSrc,
	...props
}: ImageProps) {
	const fallbackAppliedRef = useRef(false);

	return (
		<img
			alt={alt ?? ""}
			className={cn(className)}
			data-slot="image"
			{...props}
			onError={(event) => {
				const target = event.currentTarget;
				const failedSrc = target.currentSrc || target.src;

				if (fallbackSrc && !fallbackAppliedRef.current) {
					fallbackAppliedRef.current = true;
					target.src = fallbackSrc;
					return;
				}

				if (failedSrc && recordReport(failedSrc)) {
					captureException(new Error("Image failed to load"), {
						tags: { context: "image_load" },
						extra: {
							src: failedSrc,
							originalSrc: props.src,
							naturalWidth: target.naturalWidth,
							naturalHeight: target.naturalHeight,
						},
					});
				}

				onError?.(event);
			}}
		/>
	);
}

export { Image };
export type { ImageProps };
